import React, { useState, useEffect } from 'react';
import { Heart, X, Loader2, CreditCard, Wallet, Smartphone, Bitcoin, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const TIP_AMOUNTS = [1, 2, 5, 10, 20];

const PROCESSOR_ICONS = {
  'credit-card': CreditCard,
  'paypal': Wallet,
  'smartphone': Smartphone,
  'bitcoin': Bitcoin,
};

export default function TipButton({ articleId, articleTitle }) {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('amount'); // 'amount' | 'processor' | 'crypto'
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [processors, setProcessors] = useState([]);
  const [cryptoWallets, setCryptoWallets] = useState(null);
  const [copiedWallet, setCopiedWallet] = useState('');

  useEffect(() => {
    if (open && processors.length === 0) {
      fetch(`${API}/api/tips/available-processors`)
        .then(r => r.json())
        .then(data => setProcessors(data.processors || []))
        .catch(() => {});
    }
  }, [open]);

  const tipAmount = custom ? parseFloat(custom) : amount;

  const handleSelectProcessor = async (processorKey) => {
    if (!tipAmount || tipAmount <= 0) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/tips/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          article_id: articleId,
          amount: tipAmount,
          name: name || 'Anonymous',
          origin_url: window.location.origin,
          processor: processorKey,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        // Server-side detail in toast + admin error log
        const detail = data.detail || data.message || `HTTP ${res.status}`;
        const friendly = lang === 'hr'
          ? `Greška pri plaćanju (${processorKey}): ${detail}`
          : `Payment error (${processorKey}): ${detail}`;
        alert(friendly);
        // Best-effort log
        try {
          await fetch(`${API}/api/admin/error-logs`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              level: 'error', source: `tip-${processorKey}`,
              message: detail,
              stack_trace: JSON.stringify({ status: res.status, body: data, article_id: articleId, amount: tipAmount }),
            }),
          });
        } catch {}
        setSending(false);
        return;
      }

      if (processorKey === 'crypto' && data.wallets) {
        setCryptoWallets(data.wallets);
        setStep('crypto');
      } else if (processorKey === 'mypos' && data.checkout_url) {
        // Build a form and auto-submit to myPOS
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = data.checkout_url;
        for (const [key, value] of Object.entries(data.params || {})) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      console.error('Payment error:', e);
      const friendly = lang === 'hr'
        ? `Mrežna greška pri plaćanju: ${e.message || ''}`
        : `Network error during payment: ${e.message || ''}`;
      alert(friendly);
      try {
        await fetch(`${API}/api/admin/error-logs`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            level: 'error', source: `tip-${processorKey}`,
            message: `Network/Client: ${e.message || String(e)}`,
            stack_trace: e.stack || '',
          }),
        });
      } catch {}
    }
    setSending(false);
  };

  const copyWallet = (address, network) => {
    navigator.clipboard.writeText(address);
    setCopiedWallet(network);
    setTimeout(() => setCopiedWallet(''), 2000);
  };

  // Check for successful tip return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tipSuccess = params.get('tip_success');
    const sessionId = params.get('session_id');
    if (tipSuccess === 'true') {
      setDone(true);
      if (sessionId) {
        const poll = async (attempts = 0) => {
          if (attempts >= 5) return;
          try {
            const res = await fetch(`${API}/api/tips/checkout/status/${sessionId}`);
            const data = await res.json();
            if (data.payment_status === 'paid') return;
          } catch {}
          setTimeout(() => poll(attempts + 1), 2000);
        };
        poll();
      }
      const url = new URL(window.location);
      url.searchParams.delete('tip_success');
      url.searchParams.delete('session_id');
      url.searchParams.delete('provider');
      window.history.replaceState({}, '', url.pathname);
      setTimeout(() => setDone(false), 5000);
    }
  }, []);

  if (!open && !done) {
    return (
      <button onClick={() => { setOpen(true); setStep('amount'); }} data-testid="tip-button"
        className="group relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium
          bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg shadow-rose-500/25
          hover:shadow-rose-500/40 hover:scale-105 active:scale-95 transition-all duration-200">
        <Heart size={16} className="group-hover:animate-pulse" fill="currentColor" />
        <span>{lang === 'hr' ? 'Podrži članak' : 'Support this article'}</span>
      </button>
    );
  }

  if (done) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="tip-success">
        <Heart size={16} fill="currentColor" className="text-emerald-600" />
        <span>{lang === 'hr' ? 'Hvala na podršci!' : 'Thank you for your support!'}</span>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="tip-modal">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-5 w-[340px] animate-in fade-in slide-in-from-bottom-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {step !== 'amount' && (
              <button onClick={() => setStep('amount')} className="text-gray-400 hover:text-gray-600 mr-1" data-testid="tip-back-btn">
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
              <Heart size={14} className="text-white" fill="white" />
            </div>
            <h4 className="text-sm font-bold text-gray-900 dark:text-white">
              {step === 'crypto'
                ? (lang === 'hr' ? 'Kripto donacija' : 'Crypto donation')
                : step === 'processor'
                ? (lang === 'hr' ? 'Odaberi način plaćanja' : 'Choose payment method')
                : (lang === 'hr' ? 'Podrži članak' : 'Support this article')}
            </h4>
          </div>
          <button onClick={() => { setOpen(false); setStep('amount'); }} className="text-gray-400 hover:text-gray-600" data-testid="tip-close-btn"><X size={18} /></button>
        </div>

        {/* Step: Amount */}
        {step === 'amount' && (
          <>
            <div className="grid grid-cols-5 gap-2 mb-3">
              {TIP_AMOUNTS.map(a => (
                <button key={a} onClick={() => { setAmount(a); setCustom(''); }} data-testid={`tip-amount-${a}`}
                  className={`py-2 rounded-lg text-sm font-bold transition-all ${amount === a && !custom
                    ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white scale-105 shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'}`}>
                  &euro;{a}
                </button>
              ))}
            </div>
            <input type="number" placeholder={lang === 'hr' ? 'Vlastiti iznos (EUR)' : 'Custom amount (EUR)'} value={custom} onChange={e => { setCustom(e.target.value); setAmount(0); }}
              className="w-full mb-3 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm" data-testid="tip-custom-amount" />
            <input placeholder={lang === 'hr' ? 'Vaše ime (opcionalno)' : 'Your name (optional)'} value={name} onChange={e => setName(e.target.value)}
              className="w-full mb-3 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm" data-testid="tip-name-input" />
            <button onClick={() => setStep('processor')} disabled={!tipAmount || tipAmount <= 0} data-testid="tip-continue-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold
                bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg shadow-rose-500/25
                hover:shadow-rose-500/40 disabled:opacity-50 transition-all">
              {lang === 'hr' ? `Nastavi s €${tipAmount || 0}` : `Continue with €${tipAmount || 0}`}
            </button>
          </>
        )}

        {/* Step: Processor selection */}
        {step === 'processor' && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 mb-3 text-center">
              {lang === 'hr' ? `Iznos: €${tipAmount}` : `Amount: €${tipAmount}`}
            </p>
            {processors.length === 0 ? (
              <div className="text-center py-4">
                <Loader2 size={20} className="animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-xs text-gray-400">{lang === 'hr' ? 'Učitavanje...' : 'Loading...'}</p>
              </div>
            ) : processors.map(proc => {
              const Icon = PROCESSOR_ICONS[proc.icon] || CreditCard;
              return (
                <button key={proc.key} onClick={() => handleSelectProcessor(proc.key)}
                  disabled={sending} data-testid={`tip-processor-${proc.key}`}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-rose-300 hover:bg-rose-50 dark:hover:bg-gray-700 transition-all disabled:opacity-50">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-gray-600 dark:text-gray-300" />
                  </div>
                  <span className="text-sm font-medium text-gray-800 dark:text-white">{proc.label}</span>
                  {sending && <Loader2 size={14} className="animate-spin text-gray-400 ml-auto" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Step: Crypto wallets */}
        {step === 'crypto' && cryptoWallets && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500 text-center mb-2">
              {lang === 'hr' ? `Pošaljite ekvivalent €${tipAmount} na jednu od adresa:` : `Send equivalent of €${tipAmount} to one of these addresses:`}
            </p>
            {Object.entries(cryptoWallets).map(([key, wallet]) => (
              <div key={key} className="p-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700" data-testid={`crypto-wallet-${key}`}>
                <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">{wallet.network}</p>
                <div className="flex items-center gap-2">
                  <code className="text-[10px] text-gray-500 break-all flex-1">{wallet.address}</code>
                  <button onClick={() => copyWallet(wallet.address, key)}
                    className="text-xs px-2 py-1 rounded bg-navy text-white hover:bg-navy-dark flex-shrink-0" data-testid={`copy-wallet-${key}`}>
                    {copiedWallet === key ? (lang === 'hr' ? 'Kopirano!' : 'Copied!') : (lang === 'hr' ? 'Kopiraj' : 'Copy')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-gray-400 text-center mt-3">
          {lang === 'hr' ? 'Sigurno plaćanje' : 'Secure payment'}
        </p>
      </div>
    </div>
  );
}
